import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'
import type { BackfillProgress } from './use-zalo-sync'

// ─────────────────────────────────────────────────────────────────────────────
// Kéo lịch sử tin nhắn Customer Service cho gian hàng TikTok Shop — nền tảng THỨ
// HAI dùng chung hàng đợi kéo lịch sử với Zalo cá nhân (giai đoạn 2,
// docs/DONG-BO-TIN-CU-ZALO.md). Cùng hình dạng dữ liệu `BackfillProgress` và cùng
// sự kiện socket `zalo:backfill-progress` (tên giữ nguyên, dùng chung mọi nền
// tảng — xem ghi chú ở backend `emitBackfillProgress`).
// ─────────────────────────────────────────────────────────────────────────────

/** Bắt đầu kéo lịch sử cho một gian hàng TikTok Shop. */
export function useBackfillTiktokShop() {
  return useMutation<
    { message: string; accountId: string; queued: boolean },
    unknown,
    { accountId: string; maxMessages?: number }
  >({
    mutationFn: async ({ accountId, maxMessages }) => {
      const { data } = await api.post(`/tiktok-shop/${accountId}/backfill`, { maxMessages })
      return data
    },
  })
}

/** Theo dõi tiến độ backfill của một gian hàng TikTok Shop — cùng cơ chế với `useBackfillProgress` của Zalo cá nhân. */
export function useTiktokBackfillProgress(accountId: string): BackfillProgress | null {
  const [progress, setProgress] = useState<BackfillProgress | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get<BackfillProgress | { accountId: string; status: null }>(`/tiktok-shop/${accountId}/backfill/status`)
      .then(({ data }) => {
        if (cancelled || data.status === null) return
        const p = data as BackfillProgress
        setProgress(p)
        if (p.status === 'completed') {
          setTimeout(() => setProgress((cur) => (cur?.status === 'completed' ? null : cur)), 6000)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [accountId])

  useEffect(() => {
    const socket = getSocket()
    const onProgress = (p: BackfillProgress) => {
      if (p.accountId !== accountId) return
      setProgress(p)
      if (p.status === 'completed') {
        setTimeout(() => setProgress((cur) => (cur?.status === 'completed' ? null : cur)), 6000)
      }
    }
    socket.on('zalo:backfill-progress', onProgress)
    return () => {
      socket.off('zalo:backfill-progress', onProgress)
    }
  }, [accountId])

  return progress
}
